import {Injectable} from '@angular/core';
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../store';
import {ActivityAction, ActivityStatus} from "../../../activities/activity";
import {
  OptimizationDialogComponent
} from "../../../websites/website/booster/dialogs/optimization-dialog/optimization-dialog.component";
import {DbModalService} from "../../../modules/modal/modal";
import {FromManagerDialogComponent} from "../../../websites/from-manager-dialog/from-manager-dialog.component";
import {UserSubscription, UserSubscriptionCategory, UserSubscriptionType} from "../../../user/user-subscription";
import {MigrationService} from "../../../websites/website/booster/services/migration.service";
import {CreateWebsiteService} from "../../../services/local/create-website.service";
import {Config} from "../../../config/config";
import {PerformanceService} from "../../../websites/website/performance/performance.service";
import {Website} from "../../../websites/website/website";
import {UserService} from "../../../user/user.service";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {User} from "../../../user/user";
import {combineLatest} from "rxjs";
import {GoogleAnalyticsService} from "../google-analytics/google-analytics.service";
import {Subject} from "rxjs";
import {Router} from "@angular/router";
import {GlobalEventDispatcherService} from "../global-event-dispatcher/global-event-dispatcher.service";
import {GlobalEvent} from "../global-event-dispatcher/global-event";
import {
  BoosterFreePlanIntroComponent
} from "../../components/dialogs/booster-free-plan-intro/booster-free-plan-intro.component";
import {BehaviorSubject} from "rxjs/internal/BehaviorSubject";
import {OneMonthFreeComponent} from "../../../shared/components/dialogs/one-month-free/one-month-free.component";
import {Utils} from "../../../shared/utils/utils";

const SECONDARY_SUBSCRIPTION_TYPES = [
  UserSubscriptionType.ONE_PRODUCT,
  UserSubscriptionType.PREMIUM,
];

@Injectable({
  providedIn: 'root'
})
export class WebsiteConnectHelperService {

  private user: User;
  private userSubscription: UserSubscription;
  private isMigrationInProgress = false;
  private demoUser: boolean = false;

  private interrupt$ = new Subject<boolean>();
   hideSidebars = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly _router: Router,
    private readonly _store: Store<fromStore.DbStoreState>,
    private readonly _modalService: DbModalService,
    private readonly _dialogService: DialogService,
    private readonly _migrationService: MigrationService,
    private readonly _createWebsiteService: CreateWebsiteService,
    private readonly _performanceService: PerformanceService,
    private readonly _userService: UserService,
    private readonly _googleAnalyticsService: GoogleAnalyticsService,
    private _globalEventDispatcher: GlobalEventDispatcherService,
  ) {
    this.getUserData();
    this.getOngoingMigrationActivity();
  }

  private getUserData(): void {
    combineLatest(
      this._store.select(fromStore.getUser),
      this._store.select(fromStore.getUserSubscription),
    )
      .subscribe(([user, userSubscription]) => {
        this.user = user;
        this.userSubscription = userSubscription;
        this.demoUser = Utils.isDemoUser(userSubscription, user);
      });
  }

  private getOngoingMigrationActivity(): void {
    this._store.select(fromStore.getMigrationOngoingActivity)
      .subscribe(activity => this.isMigrationInProgress = !!activity && activity.status === ActivityStatus.RUNNING);
  }

  public handleConnection(connectedWebsiteId: number, pluginName: string): void {
    this._store.select(fromStore.getWebsite(connectedWebsiteId))
      .pipe(
        first(website => !!website, null)
      )
      .subscribe(website => {
        this._store.dispatch(new fromStore.LoadUserSubscription);
        this._globalEventDispatcher.dispatchEvent(GlobalEvent.connectWebsite, false);
        this._router.navigate(['/websites']);
        if (this._dialogService.hasOpenedDialog() || this._modalService.hasOpenModals() || !this.userSubscription) return;
        if (pluginName === '10webspeedoptimizer') {
          return this.showBoosterOptimizationDialog(connectedWebsiteId);
        }
        if (pluginName === '10webimageoptimizer') {
          return this.showImageOptimizerDialog(connectedWebsiteId);
        }
        if (!SECONDARY_SUBSCRIPTION_TYPES.includes(this.userSubscription.type)) {
          if (this.userSubscription.domainsPermission && !this.userSubscription.domainsPermission.canCreateWebsite) {
            if (this.userSubscription.subscriptionId === Config.defaultStarterPlanId && this.demoUser) {
              return this.showCheckoutDialog();
            }
            return this._createWebsiteService.openLimitExceededModal();
          }
          /** when user has website connected with speed optimizer and trying to migrate with manager */
          else if (website.connectedFrom === 'speed_optimizer') {
            return this._migrationService.migrate(connectedWebsiteId);
          }
          else {
            this._createWebsiteService.openMigrateWebsiteModal(connectedWebsiteId, null, this.userSubscription.category === UserSubscriptionCategory.STARTER);
          }
        } else {
          if (this.userSubscription.type === UserSubscriptionType.ONE_PRODUCT) {
            this.oneProductSubscriptionWebsiteConnect(website);
          }
          else {
            this.showConnectedDialog(website, this.userSubscription.type, this.user.connectedFromProductId);
          }
        }
      });

  }


  private showBoosterOptimizationDialog(connectedWebsiteId: number): void {
    if (!this._modalService.hasOpenModals()) {
      const modal = this._modalService.open(OptimizationDialogComponent, {hasCloseBtn: false, backdrop: 'static'});
      modal.componentInstance.websiteId = connectedWebsiteId;
      modal.componentInstance.success = false;
    }
  }

  private showConnectedDialog(website, currentSubscriptionType, pluginId): void {
    this._dialogService.addDialog(FromManagerDialogComponent, {
      websiteId: website.id,
      currentSubscriptionType: currentSubscriptionType,
      fromPluginId: pluginId,
      subscription: this.userSubscription ? this.userSubscription : ''
    })
      .subscribe(result => {
        if (result) {
          this._userService.setUserGrantAccess(true);
        }
      });
  }

  private ongoingActivityPluginConnected(website, currentSubscriptionType, productId): void {
    this._store.select(fromStore.getOngoingActivity)
      .pipe(
        takeUntil(this.interrupt$),
        filter(activity => activity?.action === ActivityAction.INSTALL_ACTIVATE
          && activity.meta.product_data.slug === this.user.planProductSlug && activity.websiteId === website.id),
      )
      .subscribe(activity => {
        switch (activity.status) {
          case ActivityStatus.DONE:
            this.showConnectedDialog(website, currentSubscriptionType, productId);
            this.interrupt$.next(true);
            // this.loadingPage = false;
            break;
          case ActivityStatus.RUNNING:
          // this.loadingPage = true;
            break;
          case ActivityStatus.FAILED:
          // this.loadingPage = false;
            break;
        }
    });
  }

  private showCheckoutDialog(): void {
    this.sendAnalytics('Free Upgrade Offer', 'Locked button click', 'Dashboard: Migrate');
    const modal = this._modalService.open(OneMonthFreeComponent, {height: 600, width: 940});
    modal.componentInstance.mode = 'creation';
  }

  private oneProductSubscriptionWebsiteConnect(website: Website): void {
    // this.loadingPage = true;
    this._store.select(fromStore.getTenWebProductsLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => this._store.select(fromStore.getTenWebProducts)),
        first()
      )
      .subscribe(products => {
        this._performanceService.getGooglePageSpeedScore(website.id);
        const premiumPlugin = this.user.planProductSlug ? (products.find(product => product.slug === this.user.planProductSlug)) : null;
        this._store.dispatch(new fromStore.InitWebsiteProductAction({
          website: website,
          products: [premiumPlugin],
          action: ActivityAction.INSTALL_ACTIVATE,
          network: 1
        }));
        this.ongoingActivityPluginConnected(website, this.userSubscription.type, premiumPlugin.productId);
      });
  }

  private sendAnalytics(category, action, label): void {
    const analyticsData = {
      eventCategory: category,
      eventAction: action,
      eventLabel: label
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private showImageOptimizerDialog(websiteId): void {
    const modal = this._modalService.open(BoosterFreePlanIntroComponent, {height: 600, width: 940});
    modal.componentInstance.websiteId = websiteId;
  }
}
